import React, { useState, useMemo } from 'react';
import { auth } from '../firebase';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { CheckCircle2 } from 'lucide-react';

// ─── Payment-method options (kept in sync with backend MANUAL_PAYMENT_METHODS) ──

export const MANUAL_PAYMENT_METHODS = [
  { value: 'check',                label: 'Check',                refHint: 'Check number'         },
  { value: 'cash',                 label: 'Cash',                 refHint: 'Receipt # / note'     },
  { value: 'ach',                  label: 'ACH',                  refHint: 'Bank reference / txn' },
  { value: 'wire',                 label: 'Wire',                 refHint: 'Wire reference'       },
  { value: 'zelle',                label: 'Zelle',                refHint: 'Confirmation #'       },
  { value: 'venmo',                label: 'Venmo',                refHint: 'Venmo note / handle'  },
  { value: 'external_credit_card', label: 'External Credit Card', refHint: 'Last 4 / auth code'   },
  { value: 'other',                label: 'Other',                refHint: 'Brief description'    },
];

const fmt$ = (n) => `$${parseFloat(n || 0).toFixed(2)}`;

const MarkPaidModal = ({ isOpen, onClose, companyId, invoice, onSuccess }) => {
  const [method, setMethod]       = useState('check');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refHint = useMemo(
    () => MANUAL_PAYMENT_METHODS.find(m => m.value === method)?.refHint || 'Reference / note',
    [method]
  );

  const canSubmit = !submitting && reference.trim().length > 0;

  const handleSubmit = async () => {
    if (!invoice?.invoiceId) return;
    const trimmedRef = reference.trim();
    if (!trimmedRef) {
      toast.error('Reference is required.');
      return;
    }
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${backendUrl}/api/invoices/${invoice.invoiceId}/mark-paid`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            companyId,
            paymentMethod: method,
            reference: trimmedRef,
          }),
        }
      );
      let data = {};
      try { data = await res.json(); } catch (_) { /* ignore */ }

      if (res.ok && data.success) {
        toast.success(
          `Marked Paid — ${MANUAL_PAYMENT_METHODS.find(m => m.value === method)?.label}` +
            (invoice.amount ? ` · ${fmt$(invoice.amount)}` : '')
        );
        onSuccess?.(data);
        onClose?.();
      } else if (res.status === 403) {
        toast.error(data.detail || 'Mark Paid requires owner or admin role.');
      } else if (res.status === 409) {
        toast.info(data.detail || 'Invoice is already paid.');
        onSuccess?.(null);
        onClose?.();
      } else {
        toast.error(data.detail || 'Failed to mark invoice as paid.');
      }
    } catch (err) {
      console.error('[MarkPaidModal] Network error:', err);
      toast.error('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !invoice) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="mark-paid-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Mark Invoice as Paid
          </DialogTitle>
          <DialogDescription>
            Record an out-of-band payment (check, cash, ACH, etc.). This flips the invoice
            to paid, writes an audit row to your payment transaction history, and fires the
            same <span className="font-mono text-xs">invoice_paid</span> playbook event as
            a Stripe payment.
          </DialogDescription>
        </DialogHeader>

        {/* Invoice summary */}
        <div
          className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between"
          data-testid="mark-paid-invoice-summary"
        >
          <div className="min-w-0">
            <p className="font-medium text-sm text-gray-900 truncate">
              {invoice.customerName || 'Unknown customer'}
            </p>
            <p className="text-[11px] text-gray-400 font-mono">
              #{(invoice.invoiceId || '').slice(-8).toUpperCase()}
            </p>
          </div>
          <p className="text-base font-semibold text-gray-900 shrink-0 ml-3">
            {fmt$(invoice.amount)}
          </p>
        </div>

        {/* Method */}
        <div className="space-y-1.5">
          <Label htmlFor="mark-paid-method-trigger" className="text-xs">Payment method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger
              id="mark-paid-method-trigger"
              data-testid="mark-paid-method-trigger"
              className="h-9"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MANUAL_PAYMENT_METHODS.map(opt => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`mark-paid-method-option-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Reference */}
        <div className="space-y-1.5">
          <Label htmlFor="mark-paid-reference" className="text-xs">
            Reference / note <span className="text-red-500">*</span>
          </Label>
          <Input
            id="mark-paid-reference"
            data-testid="mark-paid-reference"
            placeholder={refHint}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={500}
            autoFocus
          />
          <p className="text-[11px] text-gray-500">
            Required. Surfaces on the invoice + transaction history (max 500 chars).
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onClose?.()}
            disabled={submitting}
            data-testid="mark-paid-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-green-600 hover:bg-green-700"
            data-testid="mark-paid-submit-btn"
          >
            {submitting ? 'Marking…' : 'Mark Paid'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkPaidModal;
